import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ApartmentData } from 'src/lib/fliko/models/units/apartment.data';
import { ParkingData } from 'src/lib/fliko/models/units/parking.data';
import { StorageData } from 'src/lib/fliko/models/units/storage.data';
import { RentalUnit, RentalUnitType } from 'src/lib/fliko/models/units/unit.data';
import { Nullish } from 'src/lib/fliko/types/typeish.type';
import { UpdateIdOnly } from 'src/lib/fliko/types/update.type';
import { DatabaseService } from 'src/app/shared/services/database.service';

type RentalUnitDataMap = {
  [RentalUnitType.APARTMENT]: ApartmentData;
  [RentalUnitType.PARKING]: ParkingData;
  [RentalUnitType.STORAGE]: StorageData;
};

@Injectable({ providedIn: 'root' })
export class UnitService {
  constructor(private dbSrv: DatabaseService) {}

  public getRentalUnitsMap$(): Observable<Map<string, RentalUnit>> {
    return combineLatest([this.getApartments$(), this.getParkings$(), this.getStorages$()]).pipe(
      map(([apt, park, sto]) => new Map([...apt, ...park, ...sto].map((unit) => [unit.id, unit])))
    );
  }

  // #region Apartment
  /**
   * @path /apartment/$estateId/
   * @key id
   */
  private get apartment() {
    return this.dbSrv.estateFactory.getCollection<ApartmentData>('apartment/$eId');
  }

  public getApartments$(): Observable<ApartmentData[]> {
    return this.apartment.getList();
  }

  public getApartment$(id: string): Observable<ApartmentData | Nullish> {
    return this.apartment.getElement(id);
  }

  public getApartmentsOnce(): Promise<ApartmentData[]> {
    return this.apartment.onceList();
  }

  public updateApartment(apartment: UpdateIdOnly<ApartmentData>): Promise<void> {
    return this.apartment.updateElement(apartment.id, apartment);
  }

  // #endregion

  // #region Parking spot
  /**
   * @path /parking/$estateId/
   * @key id
   */
  private get parking() {
    return this.dbSrv.estateFactory.getCollection<ParkingData>('parking/$eId');
  }

  public getParkings$(): Observable<ParkingData[]> {
    return this.parking.getList();
  }

  public getParking$(id: string): Observable<ParkingData | Nullish> {
    return this.parking.getElement(id);
  }

  public getParkingsOnce(): Promise<ParkingData[]> {
    return this.parking.onceList();
  }

  public updateParking(parkingSpot: UpdateIdOnly<ParkingData>): Promise<void> {
    return this.parking.updateElement(parkingSpot.id, parkingSpot);
  }

  // #endregion

  // #region Storage
  /**
   * @path /storage/$estateId/
   * @key id
   */
  private get storage() {
    return this.dbSrv.estateFactory.getCollection<StorageData>('storage/$eId');
  }

  public getStorages$(): Observable<StorageData[]> {
    return this.storage.getList();
  }

  public getStorage$(id: string): Observable<StorageData | Nullish> {
    return this.storage.getElement(id);
  }

  public getStoragesOnce(): Promise<StorageData[]> {
    return this.storage.onceList();
  }

  public updateStorage(storage: UpdateIdOnly<StorageData>): Promise<void> {
    return this.storage.updateElement(storage.id, storage);
  }

  // #endregion

  public updateUnit<T extends RentalUnitType>(type: T, data: UpdateIdOnly<RentalUnitDataMap[T]>): Promise<void> {
    switch (type) {
      case RentalUnitType.APARTMENT:
        return this.updateApartment(data as UpdateIdOnly<ApartmentData>);
      case RentalUnitType.PARKING:
        return this.updateParking(data as UpdateIdOnly<ParkingData>);
      case RentalUnitType.STORAGE:
        return this.updateStorage(data as UpdateIdOnly<StorageData>);
      default:
        return Promise.reject(new Error(`Unsupported rental unit type: ${type}`));
    }
  }
}
